export const LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "pt", label: "Português", flag: "🇵🇹" },
  { code: "ja", label: "日本語", flag: "🇯🇵" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
] as const;

export type LangCode = (typeof LANGUAGES)[number]["code"];

type Dict = Record<string, string>;

export const T: Record<LangCode, Dict> = {
  en: {
    tagline: "Plan your day, anywhere on Earth.",
    searchPh: "Where are you exploring today?",
    useLocation: "Use my current location",
    locating: "Locating…",
    itinerary: "Today's itinerary",
    walking: "Walking",
    scooter: "E-scooter",
    bike: "Bicycle",
    car: "Car",
    transit: "Public transport",
    parkingNearby: "Parking nearby",
    minShort: "min",
    free: "Free",
    poweredBy: "Live routes via Google Maps · GBFS · OpenAI — worldwide",
    mapHint: "Global map ready · drag, zoom, route anywhere",
    stops: "stops",
    nav: "Routes",
    plan: "Plan",
    suggested: "Suggested for",
    notFound: "We couldn't find that place. Try Paris, Tokyo, New York, or Mexico City.",
    yourLocation: "Your location",
    preferences: "Your preferences",
    timeAvailable: "Time available",
    transportMode: "Transport mode",
    hours: "h",
    interests: "What do you feel like?",
    customNote: "Anything specific? (optional)",
    customNotePh: "e.g. quiet cafés, street art, vegan food…",
    regenerate: "Generate itinerary",
    regenerating: "Generating…",
    openInMaps: "Open in Maps",
    startNav: "Start navigation",
    navHint: "Guides the route inside the app",
    inAppNav: "Live route",
    stopProgress: "Stop",
    nextStop: "Next stop",
    directions: "Directions",
    previousStop: "Previous",
    arrivedStop: "I arrived here",
    finishRoute: "Finish route",
    exitNav: "Exit navigation",
    removeStop: "Remove stop",
    distanceTo: "Distance to stop",
    awaitingGPS: "Waiting for your location… Allow location access to auto-advance.",
    gpsActive: "Live tracking on",
    arrivedAuto: "You've arrived! Moving to next stop…",
    goToNow: "Go here now",
    openGoogleNav: "Navigate in Google Maps",
    followBlueLine: "Follow the blue line on the map to this stop.",
    intCultural: "Culture",
    intFood: "Food",
    intViewpoint: "Viewpoints",
    intNature: "Nature",
    intShopping: "Shopping",
    intNightlife: "Nightlife",
    intFamily: "Family",
    intHidden: "Hidden gems",
  },
  fr: {
    tagline: "Planifiez votre journée, partout sur Terre.",
    searchPh: "Où explorez-vous aujourd'hui ?",
    useLocation: "Utiliser ma position actuelle",
    locating: "Localisation…",
    itinerary: "Itinéraire du jour",
    walking: "À pied",
    scooter: "Trottinette",
    bike: "Vélo",
    car: "Voiture",
    transit: "Transports en commun",
    parkingNearby: "Parking à proximité",
    minShort: "min",
    free: "Gratuit",
    poweredBy: "Itinéraires en direct via Google Maps · GBFS · OpenAI — monde entier",
    mapHint: "Carte mondiale prête · déplacez, zoomez, tracez partout",
    stops: "étapes",
    nav: "Itinéraires",
    plan: "Planifier",
    suggested: "Suggéré pour",
    notFound: "Lieu introuvable. Essayez Paris, Tokyo, New York ou Mexico.",
    yourLocation: "Votre position",
    preferences: "Vos préférences",
    timeAvailable: "Temps disponible",
    transportMode: "Mode de transport",
    hours: "h",
    interests: "Envie de quoi ?",
    customNote: "Autre chose ? (optionnel)",
    customNotePh: "ex. cafés calmes, street art, vegan…",
    regenerate: "Générer l'itinéraire",
    regenerating: "Génération…",
    openInMaps: "Ouvrir dans Maps",
    startNav: "Démarrer la navigation",
    navHint: "Guide l’itinéraire dans l’application",
    inAppNav: "Itinéraire en direct",
    stopProgress: "Étape",
    nextStop: "Prochaine étape",
    directions: "Indications",
    previousStop: "Précédente",
    arrivedStop: "J’y suis arrivé",
    finishRoute: "Terminer l’itinéraire",
    exitNav: "Quitter la navigation",
    removeStop: "Retirer l'étape",
    distanceTo: "Distance jusqu’à l’étape",
    awaitingGPS: "En attente de votre position… Autorisez la localisation pour avancer automatiquement.",
    gpsActive: "Suivi en direct",
    arrivedAuto: "Vous êtes arrivé ! Passage à l’étape suivante…",
    goToNow: "Allez ici maintenant",
    openGoogleNav: "Naviguer dans Google Maps",
    followBlueLine: "Suivez la ligne bleue sur la carte jusqu’à cette étape.",
    intCultural: "Culture",
    intFood: "Gastronomie",
    intViewpoint: "Panoramas",
    intNature: "Nature",
    intShopping: "Shopping",
    intNightlife: "Vie nocturne",
    intFamily: "Famille",
    intHidden: "Coins secrets",
  },
  es: {
    tagline: "Planifica tu día, en cualquier parte del mundo.",
    searchPh: "¿Dónde estás explorando hoy?",
    useLocation: "Usar mi ubicación actual",
    locating: "Localizando…",
    itinerary: "Itinerario de hoy",
    walking: "Caminando",
    scooter: "Patinete",
    bike: "Bicicleta",
    car: "Coche",
    transit: "Transporte público",
    parkingNearby: "Aparcamiento cerca",
    minShort: "min",
    free: "Gratis",
    poweredBy: "Rutas en vivo vía Google Maps · GBFS · OpenAI — mundial",
    mapHint: "Mapa global listo · arrastra, acerca, traza ruta",
    stops: "paradas",
    nav: "Rutas",
    plan: "Planificar",
    suggested: "Sugerido para",
    notFound: "No encontramos ese lugar. Prueba París, Tokio, Nueva York o CDMX.",
    yourLocation: "Tu ubicación",
    preferences: "Tus preferencias",
    timeAvailable: "Tiempo disponible",
    transportMode: "Modo de transporte",
    hours: "h",
    interests: "¿Qué te apetece?",
    customNote: "¿Algo específico? (opcional)",
    customNotePh: "ej. cafés tranquilos, arte urbano, vegano…",
    regenerate: "Generar itinerario",
    regenerating: "Generando…",
    openInMaps: "Abrir en Maps",
    startNav: "Iniciar navegación",
    navHint: "Guía la ruta dentro de la app",
    inAppNav: "Ruta en directo",
    stopProgress: "Parada",
    nextStop: "Siguiente parada",
    directions: "Indicaciones",
    previousStop: "Anterior",
    arrivedStop: "Ya llegué",
    finishRoute: "Finalizar ruta",
    exitNav: "Salir de la navegación",
    removeStop: "Quitar parada",
    distanceTo: "Distancia a la parada",
    awaitingGPS: "Esperando tu ubicación… Permite el acceso para avanzar automáticamente.",
    gpsActive: "Seguimiento en vivo",
    arrivedAuto: "¡Has llegado! Pasando a la siguiente parada…",
    goToNow: "Ve aquí ahora",
    openGoogleNav: "Navegar en Google Maps",
    followBlueLine: "Sigue la línea azul del mapa hasta esta parada.",
    intCultural: "Cultura",
    intFood: "Gastronomía",
    intViewpoint: "Miradores",
    intNature: "Naturaleza",
    intShopping: "Compras",
    intNightlife: "Vida nocturna",
    intFamily: "Familia",
    intHidden: "Rincones secretos",
  },
  ja: {
    tagline: "世界中どこでも、一日をプランニング。",
    searchPh: "今日はどこを探検しますか？",
    useLocation: "現在地を使う",
    locating: "位置情報取得中…",
    itinerary: "本日の旅程",
    walking: "徒歩",
    scooter: "電動スクーター",
    bike: "自転車",
    car: "車",
    transit: "公共交通機関",
    parkingNearby: "近くの駐車場",
    minShort: "分",
    free: "無料",
    poweredBy: "Google マップ・GBFS・OpenAI による世界対応ルート",
    mapHint: "グローバル地図 · ドラッグ・ズーム・ルート検索",
    stops: "スポット",
    nav: "ルート",
    plan: "プラン",
    suggested: "おすすめ：",
    notFound: "場所が見つかりません。パリ、東京、ニューヨーク、メキシコシティをお試しください。",
    yourLocation: "現在地",
    preferences: "あなたの好み",
    timeAvailable: "使える時間",
    transportMode: "移動手段",
    hours: "時間",
    interests: "気分は？",
    customNote: "他にご希望は？（任意）",
    customNotePh: "例：静かなカフェ、ストリートアート、ビーガン…",
    regenerate: "プランを作成",
    regenerating: "作成中…",
    openInMaps: "マップで開く",
    startNav: "ナビ開始",
    navHint: "アプリ内でルートを案内します",
    inAppNav: "ライブルート",
    stopProgress: "スポット",
    nextStop: "次のスポット",
    directions: "道順",
    previousStop: "前へ",
    arrivedStop: "到着しました",
    finishRoute: "ルート終了",
    exitNav: "ナビを終了",
    removeStop: "削除",
    distanceTo: "スポットまでの距離",
    awaitingGPS: "現在地を取得中… 位置情報を許可すると自動で進みます。",
    gpsActive: "ライブ追跡中",
    arrivedAuto: "到着しました！次のスポットへ進みます…",
    goToNow: "今ここへ向かう",
    openGoogleNav: "Google マップでナビ",
    followBlueLine: "地図上の青い線に沿ってこのスポットへ進んでください。",
    intCultural: "文化",
    intFood: "グルメ",
    intViewpoint: "絶景",
    intNature: "自然",
    intShopping: "ショッピング",
    intNightlife: "ナイトライフ",
    intFamily: "ファミリー",
    intHidden: "隠れスポット",
  },
  de: {
    tagline: "Plane deinen Tag, überall auf der Welt.",
    searchPh: "Wo erkundest du heute?",
    useLocation: "Aktuellen Standort verwenden",
    locating: "Standort wird ermittelt…",
    itinerary: "Heutige Tour",
    walking: "Zu Fuß",
    scooter: "E-Scooter",
    bike: "Fahrrad",
    car: "Auto",
    transit: "Öffentliche Verkehrsmittel",
    parkingNearby: "Parkplatz in der Nähe",
    minShort: "Min",
    free: "Gratis",
    poweredBy: "Live-Routen via Google Maps · GBFS · OpenAI — weltweit",
    mapHint: "Weltkarte bereit · ziehen, zoomen, Route überall",
    stops: "Stationen",
    nav: "Routen",
    plan: "Planen",
    suggested: "Vorgeschlagen für",
    notFound: "Ort nicht gefunden. Versuche Paris, Tokio, New York oder Mexiko-Stadt.",
    yourLocation: "Dein Standort",
    preferences: "Deine Vorlieben",
    timeAvailable: "Verfügbare Zeit",
    transportMode: "Verkehrsmittel",
    hours: "Std",
    interests: "Worauf hast du Lust?",
    customNote: "Etwas Bestimmtes? (optional)",
    customNotePh: "z.B. ruhige Cafés, Street Art, vegan…",
    regenerate: "Tour erstellen",
    regenerating: "Erstelle…",
    openInMaps: "In Maps öffnen",
    startNav: "Navigation starten",
    navHint: "Führt die Route direkt in der App",
    inAppNav: "Live-Route",
    stopProgress: "Station",
    nextStop: "Nächste Station",
    directions: "Wegbeschreibung",
    previousStop: "Zurück",
    arrivedStop: "Ich bin angekommen",
    finishRoute: "Route beenden",
    exitNav: "Navigation verlassen",
    removeStop: "Entfernen",
    distanceTo: "Entfernung zur Station",
    awaitingGPS: "Warte auf deinen Standort… Erlaube den Zugriff für automatisches Weiterleiten.",
    gpsActive: "Live-Tracking aktiv",
    arrivedAuto: "Du bist angekommen! Weiter zur nächsten Station…",
    goToNow: "Jetzt hierhin gehen",
    openGoogleNav: "In Google Maps navigieren",
    followBlueLine: "Folge der blauen Linie auf der Karte bis zu dieser Station.",
    intCultural: "Kultur",
    intFood: "Essen",
    intViewpoint: "Aussicht",
    intNature: "Natur",
    intShopping: "Shopping",
    intNightlife: "Nachtleben",
    intFamily: "Familie",
    intHidden: "Geheime Orte",
  },
  pt: {
    tagline: "Planeia o teu dia, em qualquer parte do mundo.",
    searchPh: "Onde estás a explorar hoje?",
    useLocation: "Usar a minha localização",
    locating: "A localizar…",
    itinerary: "Itinerário de hoje",
    walking: "A pé",
    scooter: "Trotinete",
    bike: "Bicicleta",
    car: "Carro",
    transit: "Transportes públicos",
    parkingNearby: "Estacionamento perto",
    minShort: "min",
    free: "Grátis",
    poweredBy: "Rotas em direto via Google Maps · GBFS · OpenAI — em todo o mundo",
    mapHint: "Mapa global pronto · arrasta, faz zoom, traça rotas",
    stops: "paragens",
    nav: "Rotas",
    plan: "Planear",
    suggested: "Sugerido para",
    notFound: "Não encontrámos esse local. Tenta Lisboa, Paris, Tóquio, Nova Iorque ou Cidade do México.",
    yourLocation: "A tua localização",
    preferences: "As tuas preferências",
    timeAvailable: "Tempo disponível",
    transportMode: "Modo de transporte",
    hours: "h",
    interests: "Apetece-te o quê?",
    customNote: "Algo específico? (opcional)",
    customNotePh: "ex. cafés calmos, arte urbana, vegan…",
    regenerate: "Criar itinerário",
    regenerating: "A criar…",
    openInMaps: "Abrir no Maps",
    startNav: "Iniciar navegação",
    navHint: "Guia o percurso dentro da aplicação",
    inAppNav: "Percurso em direto",
    stopProgress: "Paragem",
    nextStop: "Próxima paragem",
    directions: "Indicações",
    previousStop: "Anterior",
    arrivedStop: "Já cheguei",
    finishRoute: "Terminar percurso",
    exitNav: "Sair da navegação",
    removeStop: "Remover paragem",
    distanceTo: "Distância até à paragem",
    awaitingGPS: "À espera da tua localização… Permite o acesso para avançar automaticamente.",
    gpsActive: "Localização em direto",
    arrivedAuto: "Chegaste! A passar à próxima paragem…",
    goToNow: "Vai agora para aqui",
    openGoogleNav: "Navegar no Google Maps",
    followBlueLine: "Segue a linha azul no mapa até esta paragem.",
    intCultural: "Cultura",
    intFood: "Gastronomia",
    intViewpoint: "Miradouros",
    intNature: "Natureza",
    intShopping: "Compras",
    intNightlife: "Vida noturna",
    intFamily: "Família",
    intHidden: "Lugares secretos",
  },
  it: {
    tagline: "Pianifica la tua giornata, ovunque nel mondo.",
    searchPh: "Cosa esplori oggi?",
    useLocation: "Usa la mia posizione attuale",
    locating: "Localizzazione…",
    itinerary: "Itinerario di oggi",
    walking: "A piedi",
    scooter: "Monopattino elettrico",
    bike: "Bicicletta",
    car: "Auto",
    transit: "Trasporto pubblico",
    parkingNearby: "Parcheggio nelle vicinanze",
    minShort: "min",
    free: "Gratis",
    poweredBy: "Percorsi in tempo reale via Google Maps · GBFS · OpenAI — in tutto il mondo",
    mapHint: "Mappa globale pronta · trascina, ingrandisci, traccia ovunque",
    stops: "tappe",
    nav: "Percorsi",
    plan: "Pianifica",
    suggested: "Suggerito per",
    notFound: "Non abbiamo trovato quel luogo. Prova Parigi, Tokyo, New York o Città del Messico.",
    yourLocation: "La tua posizione",
    preferences: "Le tue preferenze",
    timeAvailable: "Tempo disponibile",
    transportMode: "Modalità di trasporto",
    hours: "h",
    interests: "Cosa ti va di fare?",
    customNote: "Qualcosa di specifico? (opzionale)",
    customNotePh: "es. caffè tranquilli, street art, cibo vegano…",
    regenerate: "Genera itinerario",
    regenerating: "Generazione…",
    openInMaps: "Apri in Maps",
    startNav: "Avvia navigazione",
    navHint: "Guida il percorso all'interno dell'app",
    inAppNav: "Percorso in tempo reale",
    stopProgress: "Tappa",
    nextStop: "Prossima tappa",
    directions: "Indicazioni",
    previousStop: "Precedente",
    arrivedStop: "Sono arrivato",
    finishRoute: "Termina percorso",
    exitNav: "Esci dalla navigazione",
    removeStop: "Rimuovi tappa",
    distanceTo: "Distanza dalla tappa",
    awaitingGPS: "In attesa della tua posizione… Consenti l'accesso alla posizione per avanzare automaticamente.",
    gpsActive: "Tracciamento in tempo reale attivo",
    arrivedAuto: "Sei arrivato! Passaggio alla prossima tappa…",
    goToNow: "Vai qui ora",
    openGoogleNav: "Naviga con Google Maps",
    followBlueLine: "Segui la linea blu sulla mappa fino a questa tappa.",
    intCultural: "Cultura",
    intFood: "Cibo",
    intViewpoint: "Punti panoramici",
    intNature: "Natura",
    intShopping: "Shopping",
    intNightlife: "Vita notturna",
    intFamily: "Famiglia",
    intHidden: "Chicche nascoste",
  },
};

export const INTERESTS = [
  { id: "cultural", key: "intCultural" },
  { id: "food", key: "intFood" },
  { id: "viewpoint", key: "intViewpoint" },
  { id: "nature", key: "intNature" },
  { id: "shopping", key: "intShopping" },
  { id: "nightlife", key: "intNightlife" },
  { id: "family", key: "intFamily" },
  { id: "hidden", key: "intHidden" },
] as const;

export type InterestId = (typeof INTERESTS)[number]["id"];

/**
 * Resolves a UI string for `lang`, falling back to English (never to any
 * other language) when the key is missing in the target dictionary.
 */
export function t(lang: LangCode, key: keyof Dict): string {
  return T[lang]?.[key] ?? T.en[key];
}

export function formatPrice(amount: number, currency: string, lang: LangCode) {
  if (amount === 0) return T[lang].free;
  try {
    return new Intl.NumberFormat(lang, { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

// ─── Concierge public-facing naming ────────────────────────────────────────
// "Concierge" is an internal identifier only (used in the admin panel and in
// code). Anything shown to end users must use the localized "custom trip
// planning" name below, resolved from the active language.
export type ConciergeNameLang = "pt" | "en" | "es" | "fr" | "it" | "de";

export const CONCIERGE_DISPLAY_NAME: Record<ConciergeNameLang, string> = {
  pt: "Planeamento personalizado",
  en: "Custom planning",
  es: "Planificación personalizada",
  fr: "Planification personnalisée",
  it: "Pianificazione personalizzata",
  de: "Individuelle Reiseplanung",
};

/**
 * Resolves the localized, public-facing name for the concierge service.
 * Falls back to English for any language not covered above (e.g. "ja").
 */
export function getConciergeDisplayName(lang: string | null | undefined): string {
  const key = (lang ?? "").toLowerCase() as ConciergeNameLang;
  return CONCIERGE_DISPLAY_NAME[key] ?? CONCIERGE_DISPLAY_NAME.en;
}

/**
 * Resolves the language used to localize concierge-related transactional
 * emails. Falls back to Portuguese first, then English, for any
 * unrecognized/missing recipient language.
 */
export function resolveConciergeEmailLang(lang: string | null | undefined): ConciergeNameLang {
  const key = (lang ?? "").toLowerCase() as ConciergeNameLang;
  if (key in CONCIERGE_DISPLAY_NAME) return key;
  return "pt";
}

export interface ConciergeEmailCopy {
  confirmation: {
    subject: string;
    preview: (destination: string) => string;
    title: string;
    greeting: string;
    body: (destination: string) => string;
    note: string;
    button: string;
  };
  underReview: {
    subject: string;
    preview: (destination: string) => string;
    title: string;
    greeting: string;
    body: (destination: string) => string;
    note: string;
    button: string;
  };
  paymentLink: {
    subject: string;
    preview: (destination: string) => string;
    title: string;
    greeting: string;
    body: (destination: string) => string;
    valueLabel: string;
    button: string;
    statusLine: string;
  };
  delivered: {
    subject: string;
    preview: (destination: string) => string;
    title: string;
    greeting: string;
    body: (destination: string) => string;
    button: string;
    farewell: string;
  };
}

export const CONCIERGE_EMAIL_COPY: Record<ConciergeNameLang, ConciergeEmailCopy> = {
  pt: {
    confirmation: {
      subject: "Recebemos o teu pedido de planeamento — Routify",
      preview: (d) => `Recebemos o teu pedido de planeamento para ${d}`,
      title: "Recebemos o teu pedido!",
      greeting: "Olá,",
      body: (d) =>
        `O teu pedido de ${CONCIERGE_DISPLAY_NAME.pt.toLowerCase()} para ${d} foi recebido com sucesso. A equipa Routify vai analisá-lo e enviar-te uma proposta por email — normalmente em até 24h.`,
      note: 'Enquanto o pedido estiver "Novo" ou "Em análise" podes editar ou completar as informações que enviaste.',
      button: "Ver o meu pedido",
    },
    underReview: {
      subject: "O teu pedido está em análise — Routify",
      preview: (d) => `O teu pedido de planeamento para ${d} está em análise`,
      title: "Estamos a analisar o teu pedido",
      greeting: "Olá,",
      body: (d) =>
        `A equipa Routify começou a analisar o teu pedido de ${CONCIERGE_DISPLAY_NAME.pt.toLowerCase()} para ${d}. Vamos avaliar o tamanho e complexidade da rota e enviar-te uma proposta de valor por email — normalmente em até 24h.`,
      note: "Só pagas depois de aceitares a proposta.",
      button: "Ver o meu pedido",
    },
    paymentLink: {
      subject: "A tua rota personalizada está pronta",
      preview: (d) => `A tua rota personalizada para ${d} está pronta para pagamento`,
      title: "Rota personalizada pronta",
      greeting: "Olá,",
      body: (d) =>
        `A equipa Routify preparou a tua rota personalizada para ${d}. Para receberes o conteúdo final basta concluíres o pagamento seguro (Stripe).`,
      valueLabel: "Valor",
      button: "Pagar e receber rota",
      statusLine: "Podes ver o estado do teu pedido em qualquer momento aqui:",
    },
    delivered: {
      subject: "A tua rota personalizada chegou",
      preview: (d) => `A tua rota personalizada para ${d} já está disponível`,
      title: "A tua rota chegou!",
      greeting: "Olá,",
      body: (d) => `A tua rota personalizada para ${d} foi entregue. Podes ver a rota completa e descarregar o PDF na tua conta.`,
      button: "Abrir a minha rota",
      farewell: "Boa viagem — obrigado por usares a Routify!",
    },
  },
  en: {
    confirmation: {
      subject: "We received your planning request — Routify",
      preview: (d) => `We received your planning request for ${d}`,
      title: "We received your request!",
      greeting: "Hi,",
      body: (d) =>
        `Your ${CONCIERGE_DISPLAY_NAME.en.toLowerCase()} request for ${d} was received successfully. The Routify team will review it and send you a proposal by email — usually within 24h.`,
      note: 'While your request is "New" or "Under review" you can edit or complete the information you submitted.',
      button: "View my request",
    },
    underReview: {
      subject: "Your request is under review — Routify",
      preview: (d) => `Your planning request for ${d} is under review`,
      title: "We're reviewing your request",
      greeting: "Hi,",
      body: (d) =>
        `The Routify team has started reviewing your ${CONCIERGE_DISPLAY_NAME.en.toLowerCase()} request for ${d}. We'll assess the size and complexity of the route and send you a price proposal by email — usually within 24h.`,
      note: "You only pay after accepting the proposal.",
      button: "View my request",
    },
    paymentLink: {
      subject: "Your custom route is ready",
      preview: (d) => `Your custom route for ${d} is ready for payment`,
      title: "Custom route ready",
      greeting: "Hi,",
      body: (d) =>
        `The Routify team has prepared your custom route for ${d}. To receive the final content, just complete the secure payment (Stripe).`,
      valueLabel: "Amount",
      button: "Pay and get my route",
      statusLine: "You can check the status of your request anytime here:",
    },
    delivered: {
      subject: "Your custom route has arrived",
      preview: (d) => `Your custom route for ${d} is now available`,
      title: "Your route has arrived!",
      greeting: "Hi,",
      body: (d) => `Your custom route for ${d} has been delivered. You can view the full route and download the PDF in your account.`,
      button: "Open my route",
      farewell: "Have a great trip — thanks for using Routify!",
    },
  },
  es: {
    confirmation: {
      subject: "Recibimos tu solicitud de planificación — Routify",
      preview: (d) => `Recibimos tu solicitud de planificación para ${d}`,
      title: "¡Recibimos tu solicitud!",
      greeting: "Hola,",
      body: (d) =>
        `Tu solicitud de ${CONCIERGE_DISPLAY_NAME.es.toLowerCase()} para ${d} se recibió correctamente. El equipo de Routify la revisará y te enviará una propuesta por email — normalmente en hasta 24h.`,
      note: 'Mientras la solicitud esté "Nueva" o "En revisión" puedes editar o completar la información enviada.',
      button: "Ver mi solicitud",
    },
    underReview: {
      subject: "Tu solicitud está en revisión — Routify",
      preview: (d) => `Tu solicitud de planificación para ${d} está en revisión`,
      title: "Estamos revisando tu solicitud",
      greeting: "Hola,",
      body: (d) =>
        `El equipo de Routify empezó a revisar tu solicitud de ${CONCIERGE_DISPLAY_NAME.es.toLowerCase()} para ${d}. Evaluaremos el tamaño y la complejidad de la ruta y te enviaremos una propuesta de precio por email — normalmente en hasta 24h.`,
      note: "Solo pagas después de aceptar la propuesta.",
      button: "Ver mi solicitud",
    },
    paymentLink: {
      subject: "Tu ruta personalizada está lista",
      preview: (d) => `Tu ruta personalizada para ${d} está lista para el pago`,
      title: "Ruta personalizada lista",
      greeting: "Hola,",
      body: (d) =>
        `El equipo de Routify preparó tu ruta personalizada para ${d}. Para recibir el contenido final, completa el pago seguro (Stripe).`,
      valueLabel: "Importe",
      button: "Pagar y recibir mi ruta",
      statusLine: "Puedes ver el estado de tu solicitud en cualquier momento aquí:",
    },
    delivered: {
      subject: "Tu ruta personalizada ha llegado",
      preview: (d) => `Tu ruta personalizada para ${d} ya está disponible`,
      title: "¡Tu ruta ha llegado!",
      greeting: "Hola,",
      body: (d) => `Tu ruta personalizada para ${d} fue entregada. Puedes ver la ruta completa y descargar el PDF en tu cuenta.`,
      button: "Abrir mi ruta",
      farewell: "Buen viaje — ¡gracias por usar Routify!",
    },
  },
  fr: {
    confirmation: {
      subject: "Nous avons reçu ta demande de planification — Routify",
      preview: (d) => `Nous avons reçu ta demande de planification pour ${d}`,
      title: "Nous avons reçu ta demande !",
      greeting: "Bonjour,",
      body: (d) =>
        `Ta demande de ${CONCIERGE_DISPLAY_NAME.fr.toLowerCase()} pour ${d} a bien été reçue. L'équipe Routify va l'examiner et t'enverra une proposition par email — généralement sous 24h.`,
      note: 'Tant que ta demande est "Nouvelle" ou "En cours d\'examen", tu peux modifier ou compléter les informations envoyées.',
      button: "Voir ma demande",
    },
    underReview: {
      subject: "Ta demande est en cours d'examen — Routify",
      preview: (d) => `Ta demande de planification pour ${d} est en cours d'examen`,
      title: "Nous examinons ta demande",
      greeting: "Bonjour,",
      body: (d) =>
        `L'équipe Routify a commencé à examiner ta demande de ${CONCIERGE_DISPLAY_NAME.fr.toLowerCase()} pour ${d}. Nous allons évaluer la taille et la complexité de l'itinéraire et t'enverrons une proposition de prix par email — généralement sous 24h.`,
      note: "Tu ne payes qu'après avoir accepté la proposition.",
      button: "Voir ma demande",
    },
    paymentLink: {
      subject: "Ton itinéraire personnalisé est prêt",
      preview: (d) => `Ton itinéraire personnalisé pour ${d} est prêt pour le paiement`,
      title: "Itinéraire personnalisé prêt",
      greeting: "Bonjour,",
      body: (d) =>
        `L'équipe Routify a préparé ton itinéraire personnalisé pour ${d}. Pour recevoir le contenu final, il te suffit de finaliser le paiement sécurisé (Stripe).`,
      valueLabel: "Montant",
      button: "Payer et recevoir mon itinéraire",
      statusLine: "Tu peux consulter l'état de ta demande à tout moment ici :",
    },
    delivered: {
      subject: "Ton itinéraire personnalisé est arrivé",
      preview: (d) => `Ton itinéraire personnalisé pour ${d} est maintenant disponible`,
      title: "Ton itinéraire est arrivé !",
      greeting: "Bonjour,",
      body: (d) => `Ton itinéraire personnalisé pour ${d} a été livré. Tu peux consulter l'itinéraire complet et télécharger le PDF dans ton compte.`,
      button: "Ouvrir mon itinéraire",
      farewell: "Bon voyage — merci d'utiliser Routify !",
    },
  },
  it: {
    confirmation: {
      subject: "Abbiamo ricevuto la tua richiesta di pianificazione — Routify",
      preview: (d) => `Abbiamo ricevuto la tua richiesta di pianificazione per ${d}`,
      title: "Abbiamo ricevuto la tua richiesta!",
      greeting: "Ciao,",
      body: (d) =>
        `La tua richiesta di ${CONCIERGE_DISPLAY_NAME.it.toLowerCase()} per ${d} è stata ricevuta con successo. Il team Routify la esaminerà e ti invierà una proposta via email — di solito entro 24h.`,
      note: 'Finché la richiesta è "Nuova" o "In revisione" puoi modificare o completare le informazioni inviate.',
      button: "Vedi la mia richiesta",
    },
    underReview: {
      subject: "La tua richiesta è in revisione — Routify",
      preview: (d) => `La tua richiesta di pianificazione per ${d} è in revisione`,
      title: "Stiamo esaminando la tua richiesta",
      greeting: "Ciao,",
      body: (d) =>
        `Il team Routify ha iniziato a esaminare la tua richiesta di ${CONCIERGE_DISPLAY_NAME.it.toLowerCase()} per ${d}. Valuteremo dimensione e complessità dell'itinerario e ti invieremo una proposta di prezzo via email — di solito entro 24h.`,
      note: "Paghi solo dopo aver accettato la proposta.",
      button: "Vedi la mia richiesta",
    },
    paymentLink: {
      subject: "Il tuo itinerario personalizzato è pronto",
      preview: (d) => `Il tuo itinerario personalizzato per ${d} è pronto per il pagamento`,
      title: "Itinerario personalizzato pronto",
      greeting: "Ciao,",
      body: (d) =>
        `Il team Routify ha preparato il tuo itinerario personalizzato per ${d}. Per ricevere il contenuto finale basta completare il pagamento sicuro (Stripe).`,
      valueLabel: "Importo",
      button: "Paga e ricevi il mio itinerario",
      statusLine: "Puoi vedere lo stato della tua richiesta in qualsiasi momento qui:",
    },
    delivered: {
      subject: "Il tuo itinerario personalizzato è arrivato",
      preview: (d) => `Il tuo itinerario personalizzato per ${d} è ora disponibile`,
      title: "Il tuo itinerario è arrivato!",
      greeting: "Ciao,",
      body: (d) => `Il tuo itinerario personalizzato per ${d} è stato consegnato. Puoi vedere l'itinerario completo e scaricare il PDF nel tuo account.`,
      button: "Apri il mio itinerario",
      farewell: "Buon viaggio — grazie per aver usato Routify!",
    },
  },
  de: {
    confirmation: {
      subject: "Wir haben deine Planungsanfrage erhalten — Routify",
      preview: (d) => `Wir haben deine Planungsanfrage für ${d} erhalten`,
      title: "Wir haben deine Anfrage erhalten!",
      greeting: "Hallo,",
      body: (d) =>
        `Deine Anfrage für ${CONCIERGE_DISPLAY_NAME.de.toLowerCase()} für ${d} wurde erfolgreich empfangen. Das Routify-Team wird sie prüfen und dir per E-Mail ein Angebot senden — in der Regel innerhalb von 24 Stunden.`,
      note: 'Solange deine Anfrage "Neu" oder "In Prüfung" ist, kannst du die gesendeten Informationen bearbeiten oder vervollständigen.',
      button: "Meine Anfrage ansehen",
    },
    underReview: {
      subject: "Deine Anfrage wird geprüft — Routify",
      preview: (d) => `Deine Planungsanfrage für ${d} wird geprüft`,
      title: "Wir prüfen deine Anfrage",
      greeting: "Hallo,",
      body: (d) =>
        `Das Routify-Team hat begonnen, deine Anfrage für ${CONCIERGE_DISPLAY_NAME.de.toLowerCase()} für ${d} zu prüfen. Wir bewerten Umfang und Komplexität der Route und senden dir per E-Mail ein Preisangebot — in der Regel innerhalb von 24 Stunden.`,
      note: "Du zahlst erst, nachdem du das Angebot angenommen hast.",
      button: "Meine Anfrage ansehen",
    },
    paymentLink: {
      subject: "Deine individuelle Route ist fertig",
      preview: (d) => `Deine individuelle Route für ${d} ist zahlungsbereit`,
      title: "Individuelle Route fertig",
      greeting: "Hallo,",
      body: (d) =>
        `Das Routify-Team hat deine individuelle Route für ${d} vorbereitet. Um den finalen Inhalt zu erhalten, schließe einfach die sichere Zahlung ab (Stripe).`,
      valueLabel: "Betrag",
      button: "Bezahlen und Route erhalten",
      statusLine: "Du kannst den Status deiner Anfrage jederzeit hier einsehen:",
    },
    delivered: {
      subject: "Deine individuelle Route ist angekommen",
      preview: (d) => `Deine individuelle Route für ${d} ist jetzt verfügbar`,
      title: "Deine Route ist angekommen!",
      greeting: "Hallo,",
      body: (d) => `Deine individuelle Route für ${d} wurde geliefert. Du kannst die vollständige Route ansehen und das PDF in deinem Konto herunterladen.`,
      button: "Meine Route öffnen",
      farewell: "Gute Reise — danke, dass du Routify nutzt!",
    },
  },
};
